import React, { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  dot = false,
  className,
  ...props
}) => {
  const baseClasses = 'badge inline-flex items-center font-semibold rounded-full whitespace-nowrap';
  
  const variantClasses = {
    primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300',
    secondary: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  };
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-[10px] sm:text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-xs sm:text-sm',
  };
  
  const dotClasses = dot ? 'pl-1' : '';
  
  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    dotClasses,
    className
  );

  return (
    <span className={classes} {...props}>
      {dot && (
        <span
          className={clsx(
            'w-2 h-2 mr-1.5 rounded-full',
            {
              'bg-primary-500': variant === 'primary',
              'bg-gray-500': variant === 'secondary',
              'bg-green-500': variant === 'success',
              'bg-yellow-500': variant === 'warning',
              'bg-red-500': variant === 'danger',
              'bg-blue-500': variant === 'info',
            }
          )}
        />
      )}
      {children}
    </span>
  );
};

export default Badge;
